'use client'

import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react'

export type SupportedCurrency = 'NGN'

interface CurrencyContextValue {
	currency: SupportedCurrency
	setCurrency: (next: SupportedCurrency) => void
	format: (amount: number) => string
	symbol: string
}

const CurrencyContext = createContext<CurrencyContextValue | undefined>(undefined)

const STORAGE_KEY = 'landlordnoagent:currency'

function getLocaleForCurrency(_currency: SupportedCurrency): string {
	return 'en-NG'
}

function getSymbolForCurrency(_currency: SupportedCurrency): string {
	return '₦'
}

export function CurrencyProvider({ children }: { children: React.ReactNode }) {
	const [currency, setCurrencyState] = useState<SupportedCurrency>('NGN')

	useEffect(() => {
		try {
			const saved = typeof window !== 'undefined' ? (localStorage.getItem(STORAGE_KEY) as SupportedCurrency | null) : null
			if (saved === 'NGN') {
				setCurrencyState(saved)
			}
		} catch {}
	}, [])

	const setCurrency = useCallback((_next: SupportedCurrency) => {
		// Force NGN only
		setCurrencyState('NGN')
		try {
			localStorage.setItem(STORAGE_KEY, 'NGN')
		} catch {}
	}, [])

	const format = useCallback((amount: number) => {
		return new Intl.NumberFormat(getLocaleForCurrency(currency), {
			style: 'currency',
			currency,
			minimumFractionDigits: 0,
			maximumFractionDigits: 0,
		}).format(amount || 0)
	}, [currency])

	const value = useMemo<CurrencyContextValue>(() => ({
		currency,
		setCurrency,
		format,
		symbol: getSymbolForCurrency(currency)
	}), [currency, format, setCurrency])

	return (
		<CurrencyContext.Provider value={value}>{children}</CurrencyContext.Provider>
	)
}

export function useCurrency(): CurrencyContextValue {
	const ctx = useContext(CurrencyContext)
	if (!ctx) {
		throw new Error('useCurrency must be used within a CurrencyProvider')
	}
	return ctx
}



